import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function DiscountForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: '',
    applicableTo: 'membership',
    status: 'active'
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  
  useEffect(() => {
    if (isEditMode) {
      fetchDiscount();
    }
  }, [id]);
  
  const fetchDiscount = async () => {
    setIsLoading(true);
    try {
      const discount = await adminService.getDiscount(id);
      
      // Map backend fields to form fields
      setFormData({
        name: discount.name,
        description: discount.description || '',
        value: discount.value.toString(),
        applicableTo: discount.applicable_to || discount.applicableTo || 'membership',
        status: discount.status || 'active'
      });
      
    } catch (error) {
      console.error('Error fetching discount:', error);
      setSaveError('Failed to load discount data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear any previous errors for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields
    if (!formData.name) newErrors.name = 'Discount name is required';
    if (!formData.value) newErrors.value = 'Discount percentage is required';
    
    // Validate percentage value is between 0 and 100
    const percentage = parseFloat(formData.value);
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      newErrors.value = 'Percentage must be between 0 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    setSaveError(null);
    
    try {
      // Convert string values to appropriate types
      const processedData = {
        ...formData,
        value: parseFloat(formData.value)
      };
      
      if (isEditMode) {
        await adminService.updateDiscount(id, processedData);
      } else {
        await adminService.createDiscount(processedData);
      }
      
      // Navigate back to discounts list on success
      navigate('/admin/discounts');
    } catch (error) {
      console.error('Error saving discount:', error);
      
      if (error.response && error.response.data && error.response.data.error) {
        // Display API error message
        setSaveError(error.response.data.error);
      } else {
        // Generic error message
        setSaveError('An error occurred while saving the discount. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isLoading && isEditMode) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Loading Discount...</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading discount data...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">
          {isEditMode ? 'Edit Discount' : 'Add Discount'}
        </h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/discounts" className="admin-breadcrumb-link">Discounts</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">
              {isEditMode ? 'Edit' : 'Add'}
            </span>
          </li>
        </ul>
      </div>
      
      {saveError && (
        <div className="admin-alert admin-alert-danger" style={{ marginBottom: '1rem' }}>
          {saveError}
        </div>
      )}
      
      <div className="admin-card">
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="admin-row">
              <div className="admin-col-12 admin-col-md-6">
                <div className="admin-form-group">
                  <label htmlFor="name" className="admin-form-label">Discount Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.name ? 'admin-is-invalid' : ''}`}
                    placeholder="Summer Sale"
                  />
                  {errors.name && <div className="admin-invalid-feedback">{errors.name}</div>}
                  <small className="admin-form-text admin-text-muted">
                    Enter a descriptive name for this discount
                  </small>
                </div>
              </div>
              
              <div className="admin-col-12 admin-col-md-6">
                <div className="admin-form-group">
                  <label htmlFor="status" className="admin-form-label">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="admin-form-control"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="description" className="admin-form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="admin-form-control"
                rows="2"
                placeholder="Special discount for summer season"
              ></textarea>
            </div>
            
            <div className="admin-row">
              <div className="admin-col-12 admin-col-md-6">
                <div className="admin-form-group">
                  <label htmlFor="value" className="admin-form-label">Discount Percentage (%) *</label>
                  <input
                    type="number"
                    id="value"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    className={`admin-form-control ${errors.value ? 'admin-is-invalid' : ''}`}
                    placeholder="10"
                    min={0}
                    max={100}
                    step="any"
                  />
                  {errors.value && <div className="admin-invalid-feedback">{errors.value}</div>}
                  <small className="admin-form-text admin-text-muted">
                    This percentage will be automatically applied at checkout
                  </small>
                </div>
              </div>
              
              <div className="admin-col-12 admin-col-md-6">
                <div className="admin-form-group">
                  <label htmlFor="applicableTo" className="admin-form-label">Applicable To *</label>
                  <select
                    id="applicableTo"
                    name="applicableTo"
                    value={formData.applicableTo}
                    onChange={handleChange}
                    className="admin-form-control"
                  >
                    <option value="membership">Membership Plans</option>
                    <option value="listing">Professional Listing</option>
                    <option value="program">Programs</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
              <Link to="/admin/discounts" className="admin-btn admin-btn-light">
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
                Back to Discounts
              </Link>
              
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={isLoading}
              >
                <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                {isLoading ? 'Saving...' : 'Save Discount'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}

export default DiscountForm;
